import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, MenuButton, MenuItem, MenuItemDivider } from "@carbon/react";
import { DeliveryTruck } from "@carbon/icons-react";
import "./bid.scss";

export interface BidToolbarProps {
  /** Whether the user may manage the order (`can(CREATE_ORDERS)`); when false the toolbar is hidden. */
  canManageOrder: boolean;
  /** Whether the award panel (`Award`/`AwardJoint`, Task 8) is currently open — swaps this whole
   * toolbar for a single "Cancel" button, same as the pre-Carbon `!isAward ? ... : <Button>`. */
  isAward: boolean;
  onCancelAward: () => void;

  showCancelOrder: boolean;
  onCancelOrder: () => void;

  chatCount: number;
  onOpenChat: () => void;

  showInvite: boolean;
  onInvite: () => void;

  showDownloadComparison: boolean;
  onDownloadComparison: () => void;
  isDownloadingComparison: boolean;

  showDownloadBidReport: boolean;
  onDownloadBidReport: () => void;
  isDownloadingBidReport: boolean;

  showDeliveries: boolean;
  deliveriesLink: string;

  /** Awarded order with no OC yet — offer creating one (opens the Crear-OC modal scoped to it). */
  showCreateOc?: boolean;
  onCreateOc?: () => void;

  /** The award entry point (Task 8 owns the panel itself — this only renders the trigger). */
  showAwardTrigger: boolean;
  onAwardTrigger: () => void;

  showSecondRound: boolean;
  onSecondRound: () => void;

  /** SIMPLE orders only — `JointBuyerBid.tsx` has no equivalent action, so both are left
   * undefined at that call site rather than this component guessing a joint-order behaviour. */
  showFinalize?: boolean;
  onFinalize?: () => void;
  isFinalizing?: boolean;
}

/**
 * The buyer's action bar above the order header — shared by `BuyerBid.tsx` and `JointBuyerBid.tsx`.
 * The one contextual PRIMARY action (Adjudicar while a closed order still has offers, else Coordinar
 * entrega once awarded) stays a visible button; every other action collapses into a single Carbon
 * "Acciones" menu so the header no longer spreads six-plus buttons across a row. Every gating
 * condition is computed by the caller and passed in as a plain boolean — this component only renders
 * what it's told.
 */
const BidToolbar = ({
  canManageOrder,
  isAward,
  onCancelAward,
  showCancelOrder,
  onCancelOrder,
  chatCount,
  onOpenChat,
  showInvite,
  onInvite,
  showDownloadComparison,
  onDownloadComparison,
  isDownloadingComparison,
  showDownloadBidReport,
  onDownloadBidReport,
  isDownloadingBidReport,
  showDeliveries,
  deliveriesLink,
  showCreateOc,
  onCreateOc,
  showAwardTrigger,
  onAwardTrigger,
  showSecondRound,
  onSecondRound,
  showFinalize,
  onFinalize,
  isFinalizing,
}: BidToolbarProps) => {
  const { t } = useTranslation();

  if (!canManageOrder) return null;

  if (isAward) {
    return (
      <Button kind="secondary" size="sm" onClick={onCancelAward}>
        {t("cta.cancel")}
      </Button>
    );
  }

  return (
    <div className="bid-toolbar">
      {/* Contextual primary action: adjudicate a closed RFQ, or coordinate delivery once awarded
          (the two are mutually exclusive by order state). Everything else lives in the menu. */}
      {showAwardTrigger ? (
        <Button size="sm" onClick={onAwardTrigger}>
          {t("tenders.data.accept")}
        </Button>
      ) : showDeliveries ? (
        <Button
          size="sm"
          as={Link}
          to={deliveriesLink}
          renderIcon={DeliveryTruck}
        >
          {t("deliveries.new")}
        </Button>
      ) : null}

      <MenuButton size="sm" kind="tertiary" label={t("cta.actions")}>
        {showCreateOc && onCreateOc && (
          <>
            <MenuItem
              label={t("purchaseOrders.create.button")}
              onClick={onCreateOc}
            />
            <MenuItemDivider />
          </>
        )}

        <MenuItem
          label={
            chatCount > 0
              ? `${t("chat.title")} (${chatCount})`
              : t("chat.title")
          }
          onClick={onOpenChat}
        />

        {showInvite && (
          <MenuItem
            label={t("orderByRequest.inviteSupplier")}
            onClick={onInvite}
          />
        )}

        {showDownloadComparison && (
          <MenuItem
            label={t("cta.downloadComparison")}
            disabled={isDownloadingComparison}
            onClick={onDownloadComparison}
          />
        )}

        {showDownloadBidReport && (
          <MenuItem
            label={t("cta.downloadBidReport")}
            disabled={isDownloadingBidReport}
            onClick={onDownloadBidReport}
          />
        )}

        {showSecondRound && (
          <MenuItem
            label={t("tenders.data.secondRound")}
            onClick={onSecondRound}
          />
        )}

        {showFinalize && (
          <MenuItem
            label={t("tenders.data.finalizeAdjudication")}
            disabled={isFinalizing}
            onClick={onFinalize}
          />
        )}

        {showCancelOrder && (
          <>
            <MenuItemDivider />
            <MenuItem
              label={t("tenders.data.cancel")}
              kind="danger"
              onClick={onCancelOrder}
            />
          </>
        )}
      </MenuButton>
    </div>
  );
};

export default BidToolbar;
